import {
  AvailabilityType,
  DateChoicesEnum,
  KindOfTimeType,
  OrderLimitType,
  ProductOverrideType,
  WorkingDayType,
} from '@/constants/enum';
import {
  IDeliveryDisable,
  IDeliveryOverrideDateRanges,
  IDeliveryOverrideHolidays,
  IDeliveryOverrideSpecificWorkingDays,
  IDeliverySettings,
  IDeliveryState,
  IDeliveryZoneError,
} from '@/types/components/delivery';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { IHoliday, ISpecificWorkingDay, IWeeklyWorkingDay } from '@/types/components/day';

const deliverySettingInitial: IDeliverySettings = {
  id: -1,
  shop: '',
  name: '',
  city: '',
  additionalNote: '',
  locationId: -1,
  isEnable: false,
  isDefault: false,
  isActive: false,
  postalCodeValidation: {
    id: -1,
    deliverySettingId: -1,
    isEnable: true,
    exactMatch: true,
    exactMatchPostalCodes: [],
    partialMatchPostalCodes: [],
    ukPostcodeValidation: false,
  },
  deliveryChoice: DateChoicesEnum.DATETIMESLOT,
  workingDayType: WorkingDayType.WEEKDAYS,
  deliveryTimeSlotType: KindOfTimeType.SAME,
  deliveryPreparationTimeType: KindOfTimeType.SAME,
  orderLimitType: OrderLimitType.LIMIT_PER_DAY,
  deliveryDateRange: { value: '14', unit: 'day' }, // ex: { value: 1, unit: 'day' }
  overrideType: ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS,
  productAvailabilityType: AvailabilityType.ALL,
  isEnablePreparationTime: false,
  additionalTags: [], // ex: [] tag1, tag2, tag3 ]
  minimumOrderAmount: null,
  updatedAt: '',
  createdAt: '',
  deliveryRestrictionRange: 0,
};

const deliveryStateInitial: IDeliveryState = {
  isSaving: false,
  isLoading: false,
  isCheckingTimeSlot: false,
};

const deliveryDisableInitial: IDeliveryDisable = {
  isDisableSpecificProduct: false,
  isDisableProductOverride: false,
  isDisableOrderLimitPerTimeSlot: false,
  isDisableMinimumOrderValue: false,
};

const deliveryWeeklyWorkingDaysInitial: IWeeklyWorkingDay[] = [];

const deliverySpecificWorkingDaysInitial: ISpecificWorkingDay[] = [];

const deliveryHolidayInitial: IHoliday[] = [];

const deliveryZoneErrorInitial: IDeliveryZoneError = {
  exact: {},
  partial: {},
};

const deliveryProductsInitial: string[] = [];

const deliveryCollectionsInitial: string[] = [];

const deliveryOverrideSpecificWorkingDaysInitial: IDeliveryOverrideSpecificWorkingDays[] = [];

const deliveryOverrideHolidayInitial: IDeliveryOverrideHolidays[] = [];

const deliveryOverrideDateRangesInitial: IDeliveryOverrideDateRanges[] = [];

const initialState = {
  delivery: {
    deliverySettings: deliverySettingInitial,
    deliveryState: deliveryStateInitial,
    deliveryWeeklyWorkingDays: deliveryWeeklyWorkingDaysInitial,
    deliveryWeeklyWorkingDaysLastState: deliveryWeeklyWorkingDaysInitial,
    deliverySpecificWorkingDays: deliverySpecificWorkingDaysInitial,
    deliveryHolidays: deliveryHolidayInitial,
    deliveryZoneError: deliveryZoneErrorInitial,
    deliveryProducts: deliveryProductsInitial,
    deliveryCollections: deliveryCollectionsInitial,
    deliveryOverrideSpecificWorkingDays: deliveryOverrideSpecificWorkingDaysInitial,
    deliveryOverrideHolidays: deliveryOverrideHolidayInitial,
    deliveryOverrideDateRanges: deliveryOverrideDateRangesInitial,
  },
  deliveryBackup: {
    deliverySettings: deliverySettingInitial,
    deliveryState: deliveryStateInitial,
    deliveryWeeklyWorkingDays: deliveryWeeklyWorkingDaysInitial,
    deliveryWeeklyWorkingDaysLastState: deliveryWeeklyWorkingDaysInitial,
    deliverySpecificWorkingDays: deliverySpecificWorkingDaysInitial,
    deliveryHolidays: deliveryHolidayInitial,
    deliveryZoneError: deliveryZoneErrorInitial,
    deliveryProducts: deliveryProductsInitial,
    deliveryCollections: deliveryCollectionsInitial,
    deliveryOverrideSpecificWorkingDays: deliveryOverrideSpecificWorkingDaysInitial,
    deliveryOverrideHolidays: deliveryOverrideHolidayInitial,
    deliveryOverrideDateRanges: deliveryOverrideDateRangesInitial,
  },
  deliveryDisable: deliveryDisableInitial,
};

const deliverySlice = createSlice({
  name: 'delivery',
  initialState,
  reducers: {
    handleSaveDeliverySettings: (state, action: PayloadAction<Partial<IDeliverySettings>>) => {
      state.delivery.deliverySettings = { ...state.delivery.deliverySettings, ...action.payload };
    },
    handleSetDeliveryState: (state, action: PayloadAction<Partial<IDeliveryState>>) => {
      state.delivery.deliveryState = { ...state.delivery.deliveryState, ...action.payload };
    },
    handleSaveDeliveryWeeklyWorkingDays: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.delivery.deliveryWeeklyWorkingDays = action.payload;
    },
    handleSaveDeliveryWeeklyWorkingDaysLastState: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.delivery.deliveryWeeklyWorkingDaysLastState = action.payload;
    },
    handleSaveDeliverySpecificWorkingDays: (state, action: PayloadAction<ISpecificWorkingDay[]>) => {
      state.delivery.deliverySpecificWorkingDays = action.payload;
    },
    handleSaveDeliveryHolidays: (state, action: PayloadAction<IHoliday[]>) => {
      state.delivery.deliveryHolidays = action.payload;
    },
    handleSaveDeliveryProducts: (state, action: PayloadAction<string[]>) => {
      state.delivery.deliveryProducts = action.payload;
    },
    handleSaveDeliveryCollections: (state, action: PayloadAction<string[]>) => {
      state.delivery.deliveryCollections = action.payload;
    },
    handleSaveOverrideDeliverySpecificWorkingDays: (state, action: PayloadAction<IDeliveryOverrideSpecificWorkingDays[]>) => {
      state.delivery.deliveryOverrideSpecificWorkingDays = action.payload;
    },
    handleSaveOverrideDeliveryHolidays: (state, action: PayloadAction<IDeliveryOverrideHolidays[]>) => {
      state.delivery.deliveryOverrideHolidays = action.payload;
    },
    handleSaveOverrideDeliveryDateRanges: (state, action: PayloadAction<IDeliveryOverrideDateRanges[]>) => {
      state.delivery.deliveryOverrideDateRanges = action.payload;
    },
    handleSaveBackupDeliverySettings: (state, action: PayloadAction<Partial<IDeliverySettings>>) => {
      state.deliveryBackup.deliverySettings = { ...state.deliveryBackup.deliverySettings, ...action.payload };
    },
    handleSetBackupDeliveryState: (state, action: PayloadAction<Partial<IDeliveryState>>) => {
      state.deliveryBackup.deliveryState = { ...state.deliveryBackup.deliveryState, ...action.payload };
    },
    handleSaveBackupDeliveryWeeklyWorkingDays: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.deliveryBackup.deliveryWeeklyWorkingDays = action.payload;
    },
    handleSaveBackupDeliveryWeeklyWorkingDaysLastState: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.deliveryBackup.deliveryWeeklyWorkingDaysLastState = action.payload;
    },
    handleSaveBackupDeliverySpecificWorkingDays: (state, action: PayloadAction<ISpecificWorkingDay[]>) => {
      state.deliveryBackup.deliverySpecificWorkingDays = action.payload;
    },
    handleSaveBackupDeliveryHolidays: (state, action: PayloadAction<IHoliday[]>) => {
      state.deliveryBackup.deliveryHolidays = action.payload;
    },
    handleSaveBackupDeliveryProducts: (state, action: PayloadAction<string[]>) => {
      state.deliveryBackup.deliveryProducts = action.payload;
    },
    handleSaveBackupDeliveryCollections: (state, action: PayloadAction<string[]>) => {
      state.deliveryBackup.deliveryCollections = action.payload;
    },
    handleSetDeliveryZoneError: (state, action: PayloadAction<Partial<IDeliveryZoneError>>) => {
      state.delivery.deliveryZoneError = {
        ...state.delivery.deliveryZoneError,
        ...action.payload,
        exact: {
          ...state.delivery.deliveryZoneError.exact,
          ...action.payload.exact,
        },
        partial: {
          ...state.delivery.deliveryZoneError.partial,
          ...action.payload.partial,
        },
      };
    },
    handleSaveBackupOverrideDeliverySpecificWorkingDays: (
      state,
      action: PayloadAction<IDeliveryOverrideSpecificWorkingDays[]>,
    ) => {
      state.deliveryBackup.deliveryOverrideSpecificWorkingDays = action.payload;
    },
    handleSaveBackupOverrideDeliveryHolidays: (state, action: PayloadAction<IDeliveryOverrideHolidays[]>) => {
      state.deliveryBackup.deliveryOverrideHolidays = action.payload;
    },
    handleSaveBackupOverrideDeliveryDateRanges: (state, action: PayloadAction<IDeliveryOverrideDateRanges[]>) => {
      state.deliveryBackup.deliveryOverrideDateRanges = action.payload;
    },
    handleResetDeliveryInitial: (state) => {
      state.delivery = initialState.delivery;
      state.deliveryBackup = initialState.deliveryBackup;
    },
    handleResetDeliveryState: (state) => {
      state.delivery.deliveryState.isSaving = deliveryStateInitial.isSaving;
      state.delivery.deliveryState.isLoading = deliveryStateInitial.isLoading;
      state.delivery.deliveryState.isCheckingTimeSlot = deliveryStateInitial.isCheckingTimeSlot;
    },
    handleResetDelivery: (state) => {
      state.delivery = state.deliveryBackup;
    },
    handleSetDeliveryDisable: (state, action: PayloadAction<Partial<IDeliveryDisable>>) => {
      state.deliveryDisable = { ...state.deliveryDisable, ...action.payload };
    },
  },
});

export const deliverySettingSelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliverySettings,
);
export const deliveryStateSelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliveryState,
);
export const deliveryWeeklyWorkingDaySelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliveryWeeklyWorkingDays,
);
export const deliveryWeeklyWorkingDaysLastStateSelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliveryWeeklyWorkingDaysLastState,
);
export const deliverySpecificWorkingDaySelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliverySpecificWorkingDays,
);
export const deliveryHolidaySelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliveryHolidays,
);
export const deliveryProductSelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliveryProducts,
);
export const deliveryCollectionSelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliveryCollections,
);
export const deliveryOverrideSpecificWorkingDaysSelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliveryOverrideSpecificWorkingDays,
);
export const deliveryOverrideHolidaysSelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliveryOverrideHolidays,
);
export const deliveryOverrideDateRangesSelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliveryOverrideDateRanges,
);
export const deliveryZoneErrorSelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.delivery.deliveryZoneError,
);
export const currentDeliverySelector = createSelector(
  (state: RootState) => state.delivery,
  (delivery) => delivery.delivery,
);
export const oldDeliverySelector = createSelector(
  (state: RootState) => state.delivery,
  (state) => state.deliveryBackup,
);
export const deliveryDisableSelector = createSelector(
  (state: RootState) => state.delivery.deliveryDisable,
  (deliveryDisable) => deliveryDisable,
);

export const {
  handleSaveDeliverySettings,
  handleSetDeliveryState,
  handleSaveDeliveryWeeklyWorkingDays,
  handleSaveDeliveryWeeklyWorkingDaysLastState,
  handleSaveDeliverySpecificWorkingDays,
  handleSaveDeliveryHolidays,
  handleSaveDeliveryProducts,
  handleSaveDeliveryCollections,
  handleSaveOverrideDeliverySpecificWorkingDays,
  handleSaveOverrideDeliveryHolidays,
  handleSaveOverrideDeliveryDateRanges,
  handleSaveBackupDeliverySettings,
  handleSetBackupDeliveryState,
  handleSaveBackupDeliveryWeeklyWorkingDays,
  handleSaveBackupDeliveryWeeklyWorkingDaysLastState,
  handleSaveBackupDeliverySpecificWorkingDays,
  handleSaveBackupDeliveryHolidays,
  handleSaveBackupDeliveryProducts,
  handleSaveBackupDeliveryCollections,
  handleSaveBackupOverrideDeliverySpecificWorkingDays,
  handleSaveBackupOverrideDeliveryHolidays,
  handleSaveBackupOverrideDeliveryDateRanges,
  handleResetDeliveryState,
  handleResetDeliveryInitial,
  handleResetDelivery,
  handleSetDeliveryZoneError,
  handleSetDeliveryDisable,
} = deliverySlice.actions;

export default deliverySlice;
